import { DateTimeFormatter, type ZonedDateTime } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import clsx from 'clsx';
import React from 'react';
import { Button } from 'react-aria-components';
import SignaturePad from 'signature_pad';

import { focusRingClassName } from '#/shared/utils/domHelper';

import { SignatureInputDrawEmpty } from './SignatureInputDrawEmpty';

type SignatureInputDrawCanvasProps = {
  value?: string;
  signedAt?: ZonedDateTime;
  readOnly?: boolean;
  isError?: boolean;
  onPress?: () => void;
  onChange?: (value: string | undefined) => void;
};

export function SignatureInputDrawCanvas({
  value,
  signedAt,
  readOnly,
  isError,
  onPress,
  onChange,
}: Readonly<SignatureInputDrawCanvasProps>) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = React.useRef<HTMLDivElement>(null);
  const signaturePadRef = React.useRef<SignaturePad | null>(null);

  const hasContent = !!value;
  const actionButtonLabel = readOnly ? 'Edit' : 'Clear';
  const isActionButtonDisabled = !hasContent && !readOnly;

  /**
   * Resizes the canvas to fit the container.
   */
  const resizeCanvas = React.useCallback(() => {
    if (!readOnly && canvasContainerRef.current && canvasRef.current && signaturePadRef.current) {
      canvasRef.current.width = canvasContainerRef.current.offsetWidth;
      canvasRef.current.height = canvasContainerRef.current.offsetWidth / 2;
      // canvas is cleared on resize by browser, we need to clear the lib value as well
      signaturePadRef.current.clear();
    }
  }, [readOnly]);

  /**
   * Calls onChange at the end of a stroke on the signature pad.
   */
  const handleStrokeEnd = React.useCallback(() => {
    if (onChange) {
      onChange(signaturePadRef.current?.toDataURL());
    }
  }, [onChange]);

  /**
   * Initializes the signature pad when the component mounts or when the `readOnly` changes.
   */
  React.useEffect(() => {
    if (readOnly) {
      signaturePadRef.current = null;
    } else if (canvasRef.current) {
      signaturePadRef.current = new SignaturePad(canvasRef.current);
      resizeCanvas();
      if (value) {
        signaturePadRef.current.fromDataURL(value, { ratio: 1 });
      }
    }
  }, [value, readOnly, resizeCanvas]);

  /**
   * Resizes the canvas when the window is resized to fit it correctly in the container.
   */
  React.useEffect(() => {
    window.addEventListener('resize', resizeCanvas);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [resizeCanvas]);

  /**
   * Listen to the signature pad updates.
   */
  React.useEffect(() => {
    signaturePadRef.current?.addEventListener('endStroke', handleStrokeEnd);
    return () => {
      signaturePadRef.current?.removeEventListener('endStroke', handleStrokeEnd);
    };
  }, [handleStrokeEnd]);

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      if (onChange) {
        onChange(undefined);
      }
    }
  };

  if (readOnly && !hasContent) {
    return <SignatureInputDrawEmpty onPress={onPress} isError={isError} />;
  }

  return (
    <div className={clsx('flex flex-col w-full')}>
      {!readOnly && (
        <div className="color-scheme-only-light bg-surface-l0 rounded-sm" ref={canvasContainerRef}>
          <canvas ref={canvasRef} data-testid={'signature-drawing'} />
        </div>
      )}

      {!!readOnly && (
        <div className="color-scheme-only-light bg-surface-l0 rounded-sm flex flex-col w-full">
          <img src={value} alt={''} data-testid={'signature-displaying'} />
        </div>
      )}

      <p
        className={clsx(
          'text-on-surface-variant font-500 text-center',
          'pt-12 pb-12 ml-16 mr-16',
          'border-t-1 border-outline-l2 border-dashed',
        )}
      >
        {signedAt
          ? `Signed on ${signedAt.format(DateTimeFormatter.ofPattern('dd MMM yyyy HH:mm:ss').withLocale(Locale.ENGLISH))}`
          : 'Sign above the dotted line'}
      </p>

      <Button
        className={clsx(
          'block',
          'pl-16 pr-16 pt-12 pb-12 ml-16 mr-16',
          'border-t-1',
          'border-outline-l2',
          'bg-surface-l0',
          'text-primary',
          'hover:text-primary-shade hover:cursor-pointer',
          `focus:text-primary-shade ${focusRingClassName}`,
          'disabled:text-on-surface-variant',
        )}
        onPress={readOnly ? onPress : clearSignature}
        isDisabled={isActionButtonDisabled}
      >
        {actionButtonLabel}
      </Button>
    </div>
  );
}
